define([
    "dojo/_base/declare",
    "dojo/_base/event",
    "dojo/_base/lang",
    "dojo/dom",
    "dojo/dom-attr",
    "dojo/dom-class",
    "dojo/dom-construct",
    "dojo/dom-geometry",
    "dojo/dom-style",
    "dojo/Evented",
    "dojo/keys",
    "dojo/mouse",
    "dojo/on",
    "dojo/touch",
    "MW/layout/BorderBar",
    "MW/layout/BorderContainer",
    "MW/layout/ButtonBar",
    "MW/layout/LayoutChildProperty",
    "MW/layout/LayoutContainerEvent",
    "MW/layout/LayoutUtils",
    "MW/layout/LendingTabContainer",
    "MW/layout/MaximizingContainer",
    "MW/layout/PanelContainer",
    "MW/layout/Restorable",
    "MW/layout/SelectionBlocker",
    "MW/layout/TabContainer",
    "MW/layout/TitleBar",
    "mw-log/Log",
    "dojo/i18n!l10n/nls/layout"
], function (declare, event, lang, dom, domAttr, domClass, domConstruct, domGeometry, domStyle, Evented, keys, mouse, on,
             touch, BorderBar, BorderContainer, ButtonBar, LayoutChildProperty, LayoutContainerEvent, LayoutUtils,
             LendingTabContainer, MaximizingContainer, PanelContainer, Restorable, SelectionBlocker, TabContainer, TitleBar,
             Log, layoutL10n) {

    /*
     * Subclass the Tab/TitleBar to make it adopt the child's isMaximizable property.  One might expect
     * this property to be set when the child is added but a Tab/TitleBar is not necessarily constructed
     * at that point.
     */
    var CustomTitleBar = declare([TitleBar], {
        constructor: function (args) {
            if (args.properties) {
                this.isMaximizable = args.properties.isMaximizable;
            }
        }
    });

    /**
     * A BottomContainer is a collapsible container designed to display panels along the bottom of a BorderContainer.
     * It hosts the panels within a TabContainer.  This TabContainer is placed in the center of the BottomContainer, which
     * is itself a BorderContainer.  This allows a title bar to appear at the top when there is only one
     * panel in the container and also allows a footer to appear at the bottom.  When the BottomContainer is
     * collapsed a BorderBar, displaying a button per panel, is shown.  The footer, if any is transferred to
     * the BorderBar so that it remains visible.
     */
    var BottomContainer = declare([BorderContainer, PanelContainer, Restorable], {

        enableOnTopExpand: true,

        // _tabContainer
        // _bottomBar
        // _footer
        // _footerContainer

        constructor: function () {
            this.region = "bottom";
        },

        buildRendering: function () {
            var that = this;
            this.inherited(arguments);
            domClass.add(this.domNode, "bottomContainer");

            // Add a tab container for panels
            this._tabContainer = new LendingTabContainer( {
                isContentBorderVisible: false,
                tabReorderingEnabled: true,
                region: "center",
                distributeTabs: true,
                tabAnimationEnabled: false,
                tabClass: CustomTitleBar
            });
            this._tabContainer._tabBar.dragOffEdge = "top";
            this._tabContainer._tabBar.entryExitEdge = null;
            this._tabContainer._tabBar.setGap = 0;
            this.addChild(this._tabContainer);

            // Forward tab container events
            this._tabContainer.on(LayoutContainerEvent.DRAG_OUT, function (eventData) {
                that.emit(LayoutContainerEvent.DRAG_OUT, eventData);
            });
            this._tabContainer.on(LayoutContainerEvent.STATE_CHANGED, function (eventData) {
                that.emit(LayoutContainerEvent.STATE_CHANGED, eventData);
            });
            this._tabContainer.on(LayoutContainerEvent.CHILD_ADDED, function (widget, properties) {
                if (properties.watch) {
                    properties._maximizeWatch = properties.watch(LayoutChildProperty.IS_MAXIMIZED, function (name, oldValue, newValue) {
                        if (that._switchingTabs || !that._started) {
                            return;
                        }
                        if (newValue) {
                            MaximizingContainer.maximizeGlobally(widget);
                        } else {
                            MaximizingContainer.restoreGlobally(widget);
                        }
                    });
                }
            });
            this._tabContainer.on(LayoutContainerEvent.CHILD_REMOVED, function (widget, properties) {
                if (that._bottomBar) {
                    that._bottomBar.cancelLoan(properties);
                }
                if (properties._maximizeWatch) {
                    properties._maximizeWatch.remove();
                    delete properties._maximizeWatch;
                }
                Evented.prototype.emit.apply(that, [LayoutContainerEvent.CHILD_REMOVED, widget, properties]);
            });
            this._tabContainer.watch(TabContainer.SHOWING_CHILD, function (name, oldChild, newChild) {
                if (oldChild) {
                    var oldProperties = that._tabContainer.getChildProperties(oldChild);
                    if (oldProperties && oldProperties.get(LayoutChildProperty.IS_MAXIMIZED)) {
                        that._switchingTabs = true;
                        oldProperties.set(LayoutChildProperty.IS_MAXIMIZED, false);
                        if (newChild) {
                            that._tabContainer.getChildProperties(newChild).set(LayoutChildProperty.IS_MAXIMIZED, true);
                        }
                        that._switchingTabs = false;
                    }
                }
            });

            // Add a container for the footer and a collapse all button at right
            if (this.supportFooter) {
                this._footerContainer = new ButtonBar({
                    id: this.id + "FooterContainer",
                    region: "bottom"
                });
                domClass.add(this._footerContainer.domNode, "footerContainer");
                // TODO: Base height on footer
                domStyle.set(this._footerContainer.domNode, {height: "22px"});
                this.addChild(this._footerContainer);
                if (!this.borderOptions || this.borderOptions.isCollapsible !== false) {
                    this._collapseAllButton = domConstruct.create("div", {id: this.id + "BottomCollapseAllButton", role: "button"});
                    domClass.add(this._collapseAllButton, "collapseButton");
                    domAttr.set(this._collapseAllButton, "title", layoutL10n.collapseBottomPanel);
                    dom.setSelectable(this._collapseAllButton, false);
                    LayoutUtils.preventFocus(this._collapseAllButton);
                    this._footerContainer.setPostNeighbor(this._collapseAllButton);
                    on(this._collapseAllButton, "click", function () {
                        that.emit(LayoutContainerEvent.COLLAPSE_ALL);
                    });
                    this.own(on(this._collapseAllButton, "keyup", lang.hitch(this, "_collapseAllButtonKeyUpHandler")));
                    this.own(on(this._collapseAllButton, "keydown", lang.hitch(this, "_collapseAllButtonKeyDownHandler")));
                    this.own(on(this._collapseAllButton, "focus", lang.hitch(this, function () {
                        that._collapseAllButton.classList.add("collapseButtonFocused");
                    })));
                    this.own(on(this._collapseAllButton, "blur", lang.hitch(this, function () {
                        that._collapseAllButton.classList.remove("collapseButtonFocused");
                    })));
                    this._collapseAllButton.tabIndex = -1;
                }
            } else {
                domClass.add(this.domNode, "notSupportingFooter");
            }

            // Create the companion bottom bar
            if (!this.borderOptions || this.borderOptions.isCollapsible !== false) {
                this._bottomBar = new BorderBar({
                    id: this.id + "BottomBar",
                    region: "bottom",
                    animationEnabled: this.animationEnabled
                });
                if (!this.supportFooter) {
                    domClass.add(this._bottomBar.domNode, "notSupportingFooter");
                }
                this._buttonBar = this._bottomBar;
                if (this._collapseAllButton) {
                    this._bottomBar.collapseAllButton = this._collapseAllButton;
                }
                if (this.enableOnTopExpand) {
                    this.collapsedWidget = this._bottomBar;  // Alias by which the BorderContainer finds the collapsed widget
                }
                this._bottomBar.on(LayoutContainerEvent.DRAG_OUT, function (eventData) {
                    eventData.widget = eventData.properties.widget;
                    that.emit(LayoutContainerEvent.DRAG_OUT, eventData);
                });

                this._bottomBar.on(LayoutContainerEvent.EXPAND_ALL, function () {
                    that.emit(LayoutContainerEvent.EXPAND_ALL);
                });
                this._bottomBar.on(LayoutContainerEvent.STATE_CHANGED, function () {
                    that.emit(LayoutContainerEvent.STATE_CHANGED);
                });
            }
        },

        getCollapseAllButton: function() {
            return this._collapseAllButton;
        },

        addPanel: function (widget, properties) {
            var i, restoreIndex, restoreInfo, immediate, childrenToRestore = [];

            if (this.isRestoringState()) {
                var addPanelId = properties ? properties.panelId : widget.panelId;
                immediate = true;
                // Find the entry in the state to restore corresponding to the panel being added.
                // Set the panel index based on the number of preceding, previously installed panels.
                restoreIndex = 0;
                childrenToRestore = this._stateToRestore.children || this._stateToRestore.panels;
                for (i = 0; i < childrenToRestore.length; i++) {
                    restoreInfo = childrenToRestore[i];
                    if (restoreInfo.id === addPanelId) {
                        restoreInfo.restored = true;
                        if (restoreInfo.showing || restoreInfo.selected) {  // selected is grandfathered
                            this._pendingShow = widget;
                        }
                        break;
                    } else if (restoreInfo.restored) {
                        restoreIndex++;
                    }
                }
                if (restoreIndex === childrenToRestore.length) {
                    // The panel was not found.  Declare the restoration over
                    this.endStateRestore();
                    restoreIndex = undefined;
                }
            }

            if (properties) {
                this._tabContainer.addWithProperties(widget, properties, restoreIndex, true, immediate);
            } else {
                this._tabContainer.addChild(widget, restoreIndex, true, immediate);
            }

            if (this.isRestoringState() && childrenToRestore.length === this.getPanelCount()) {
                this.endStateRestore();
            }

            properties = properties || widget;
            properties.widget = widget;
            if (this._bottomBar) {
                this._bottomBar.acceptLoan(this._tabContainer, properties);
            }
            this._updateSingeTabVisibility();

            if (this.isContainerCollapsed()) {
                properties.set(LayoutChildProperty.IS_SHOWING, false);
            }

            this.emit(LayoutContainerEvent.CHILD_ADDED, widget, properties);
        },

        removePanel: function (widget, properties) {
            this._tabContainer.removeChild(widget, true /* immediate */);
            this._updateSingeTabVisibility();
        },

        removeAll: function () {
            this._tabContainer.removeAll();
            if (this._bottomBar) {
                this._bottomBar.removeAll();
            }
        },

        getPanels: function () {
            return this._tabContainer.getChildrenInTabOrder();
        },

        getPanelProperties: function() {
            var panels = this.getPanels();
            var properties = [];
            properties.length = panels.length;
            for (var i = 0; i < panels.length; i++) {
                properties[i] = this._tabContainer.getChildProperties(panels[i]);
            }
            return properties;
        },

        getPanelCount: function () {
            return this._tabContainer.getTabCount();
        },

        revealChild: function (widget) {
            /* Causes stack overflow
            this._tabContainer.revealChild(widget);
            */
        },

        nextChild: function (widget, showing, forwardNavigationKeyPressed) {
            var parent = this.getParent();
            if (parent && parent.isCollapsed && parent.isCollapsed("bottom")) {
                return this._tabContainer.nextChild(widget, showing);
            } else {
                return this._tabContainer.nextChild(widget, showing, forwardNavigationKeyPressed);
            }
        },

        previousChild: function (widget, showing, backwardNavigationKeyPressed) {
            var parent = this.getParent();
            if (parent && parent.isCollapsed && parent.isCollapsed("bottom")) {
                return this._tabContainer.previousChild(widget, showing);
            } else {
                return this._tabContainer.previousChild(widget, showing, backwardNavigationKeyPressed);
            }
        },

        getState: function (setId) {
            var state = {children: []};
            var tabContainer = this._tabContainer;
            tabContainer.getChildrenInTabOrder().forEach(function (child) {
                var properties = tabContainer.getChildProperties(child);
                if (setId === properties.setId) {
                    var panelInfo = {id: properties ? properties.panelId : child.panelId};
                    if (child === tabContainer.get(TabContainer.SHOWING_CHILD)) {
                        panelInfo.showing = true;
                    }
                    state.children.push(panelInfo);
                }
            }, this);
            return state;
        },

        setState:function (state, setId) {
            var tabContainer = this._tabContainer;
            var properties, i, child;
            var removedChildren = [];
            var removedProperties = [];
            var stateChildren = state.children || state.panels;
            if (stateChildren.length > 0) {
                // Remove any existing children belonging to the specified panel set
                var existingChildren = tabContainer.getChildrenInTabOrder();
                for (i = 0; i < existingChildren.length; i++) {
                    child = existingChildren[i];
                    properties = tabContainer.getChildProperties(child);
                    if (properties.setId === setId) {
                        this.removePanel(child, properties);
                        removedChildren.push(child);
                        removedProperties.push(properties);
                    }
                }
                this._stateToRestore = state;
                // Add the children back so they end up in the right order
                for (i = 0; i < removedChildren.length; i++) {
                    this.addPanel(removedChildren[i], removedProperties[i]);
                }
            }
        },

        isRestoringState: function () {
            return this._stateToRestore !== undefined;
        },

        endStateRestore: function () {
            if (this._pendingShow) {
                this._tabContainer.showChild(this._pendingShow);
                delete this._pendingShow;
            }
            delete this._stateToRestore;
        },

        isExpecting: function (panelId) {
            return this._stateToRestore && BottomContainer.stateIncludes(this._stateToRestore, panelId);
        },

        setFooter: function (widget) {
            if (this._footer !== widget) {
                this._footer = widget;
                var parent = this.getParent();
                if (widget === null) {
                    if (this._bottomBar) {
                        this._bottomBar.setFiller(null);
                    }
                    this._footerContainer.setFiller(null);
                }
                else if (parent && parent.isCollapsed && parent.isCollapsed("bottom")) {
                    if (this._bottomBar) {
                        this._bottomBar.setFiller(widget);
                    }
                } else {
                    this._footerContainer.setFiller(widget);
                }
            }
        },

        getFooter: function () {
            return this._footer;
        },

        _setHasNeighborAboveAttr: function (hasNeighbor) {
            this._set("hasNeighborAbove", hasNeighbor);
            this._updateSingeTabVisibility();
            this._tabContainer._tabBar.dragOffEdge = hasNeighbor ? "top" : null;
            if (this._collapseAllButton) {
                domStyle.set(this._collapseAllButton, {visibility: hasNeighbor ? "visible" : "hidden"});
            }
        },

        _updateSingeTabVisibility: function() {
            this._tabContainer.set(TabContainer.HIDE_SINGLE_TAB, !this.hasNeighborAbove || !!this._hasSinglePanelWithoutTitle());
        },

        _hasSinglePanelWithoutTitle: function () {
            if (this._tabContainer.getTabCount() !== 1) {
                return false;
            }
            var panel = this._tabContainer.getChildrenInTabOrder()[0];
            return panel && !this._tabContainer.getChildProperties(panel).title;
        },

        collapseChange: function (isCollapsed) {
            this.inherited(arguments);
            if (this._footer) {
                if (isCollapsed) {
                    this._footerContainer.setFiller(null);
                    this._bottomBar.setFiller(this._footer);
                } else {
                    this._bottomBar.setFiller(null);
                    this._footerContainer.setFiller(this._footer);
                }
            }
            var panel = this._tabContainer.get(TabContainer.SHOWING_CHILD);
            if (panel) {
                this._tabContainer.getChildProperties(panel).set(LayoutChildProperty.IS_SHOWING, !isCollapsed);
            }
        },

        dragIn: function (dragOutInfo, mousePoint) {
            // Drag the panel into whichever is showing, the tab container or the bottom bar.
            var reCollapse;
            if (this.isOrWasContainerCollapsed(dragOutInfo)) {
                // Add to tab container and drag into bottom bar
                this._tabContainer.addWithProperties(dragOutInfo.widget, dragOutInfo.properties);
                if (this._bottomBar) {
                    this._bottomBar.dragChildIn(this._tabContainer, dragOutInfo, mousePoint);
                }
                reCollapse = true;
            } else {
                this._tabContainer.dragIn(dragOutInfo, mousePoint);
                if (this._bottomBar) {
                    this._bottomBar.acceptLoan(this._tabContainer, dragOutInfo.properties);
                }
            }
            this.emit(LayoutContainerEvent.CHILD_ADDED, dragOutInfo.widget, dragOutInfo.properties);
            // Recollapse must come after emitting the add event to ensure that the contain is parented
            if (reCollapse) {
                this.reCollapseContainer(dragOutInfo);
            }

            if (this.isContainerCollapsed()) {
                dragOutInfo.properties.set(LayoutChildProperty.IS_SHOWING, false);
            }
        },

        _collapseAllButtonKeyUpHandler: function (e) {
            if (e.keyCode === keys.SPACE) {
                e.preventDefault();
                this._collapseBottomContainer();
            }
        },

        _collapseAllButtonKeyDownHandler: function (e) {
            if (e.keyCode === keys.ENTER) {
                this._collapseBottomContainer();
            }
        },

        _collapseBottomContainer: function () {
            var parent, selectionManager, that = this;

            parent = this;
            while (!(parent && parent.selectionManager)) {
                parent = parent.getParent();
            }
            selectionManager = parent.selectionManager;

            selectionManager.blockSuccession();

            that.emit(LayoutContainerEvent.COLLAPSE_ALL);
            setTimeout(function() {
                selectionManager.unblockSuccession();
                if (that._bottomBar && that._bottomBar.getPostNeighbor) {
                    that._bottomBar.getPostNeighbor().tabIndex = -1;
                    that._bottomBar.getPostNeighbor().focus();
                }
            }, 400);
        }
    });

    /**
     * Static helper function that returns true if the given container state object includes the specified panel ID
     */
    BottomContainer.stateIncludes = function (state, panelId) {
        var stateChildren = state.children || state.panels;
        if (!stateChildren) {
            return false;
        }
        var index;
        for (index = 0; index < stateChildren.length; index += 1) {
            var id = stateChildren[index].id || stateChildren[index].panelId
            if (id === panelId) {
                return true;
            }
        }
        return false;
    };

    return BottomContainer;
});
